/**
 * Progressive copy while the playback surface is empty (animated lockup) but the queue is ready.
 * Keeps the first moments quiet; after a short wait shows status so the screen does not look frozen.
 */

/** Show the first status line after the lockup has been alone this long. */
export const PLAYBACK_SURFACE_LOADING_LABEL_AFTER_MS = 2_500

/** Escalate to a longer reassurance line after this empty duration. */
export const PLAYBACK_SURFACE_LOADING_REASSURE_AFTER_MS = 8_000

export interface IPlaybackSurfaceLoadingFeedback {
  /** When false, only the animated Lucimark lockup is shown. */
  showStatus: boolean
  headline: string
  detail: string | null
  /**
   * Download job started but no byte progress yet (common on 304 revalidate / no Content-Length).
   * UI should pulse the bar instead of showing a stuck 0%.
   */
  indeterminateProgress: boolean
}

export function resolvePlaybackSurfaceLoadingFeedback(input: {
  emptyForMs: number
  /** 0–1 download progress when known; null/NaN when not downloading. */
  downloadProgress01: number | null
}): IPlaybackSurfaceLoadingFeedback {
  const emptyForMs = Math.max(0, input.emptyForMs)
  const progress = input.downloadProgress01
  const downloading
    = progress != null && Number.isFinite(progress) && progress >= 0 && progress < 1
  const hasPercent = downloading && progress > 0
  const indeterminate = downloading && progress === 0
  const reassureDetail = emptyForMs >= PLAYBACK_SURFACE_LOADING_REASSURE_AFTER_MS
    ? 'Aguarde — a preparar a tela.'
    : null

  if (emptyForMs < PLAYBACK_SURFACE_LOADING_LABEL_AFTER_MS) {
    return {
      showStatus: false,
      headline: 'A carregar programação',
      detail: null,
      indeterminateProgress: false
    }
  }

  if (hasPercent) {
    const pct = Math.min(99, Math.max(1, Math.round(progress * 100)))
    return {
      showStatus: true,
      headline: `A carregar mídia ${pct}%`,
      detail: reassureDetail,
      indeterminateProgress: false
    }
  }

  if (indeterminate) {
    return {
      showStatus: true,
      headline: 'A carregar mídia…',
      detail: reassureDetail,
      indeterminateProgress: true
    }
  }

  if (emptyForMs >= PLAYBACK_SURFACE_LOADING_REASSURE_AFTER_MS) {
    return {
      showStatus: true,
      headline: 'Ainda a preparar',
      detail: 'Aguarde — a programação está a chegar à tela.',
      indeterminateProgress: false
    }
  }

  return {
    showStatus: true,
    headline: 'A carregar programação',
    detail: null,
    indeterminateProgress: false
  }
}
